/**
 * MobileFavoritesRow
 *
 * Mobile-optimized favorites row for the Planning module.
 * Shows sample shifts marked as `is_favorite`.
 * Chips can be pressed-and-dragged onto the scheduler grid (same as MobileSamplesRow).
 * Each chip has a remove button to un-favorite.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { setDraggingItem } from "../planningSlice";
import {
  addFavoriteShiftSample,
  deleteFavoriteShiftSample,
} from "../../../components/scheduler/shifts/sampleShiftsSlice";
import {
  getSampleColor,
  getSampleLabel,
  formatMinutes,
} from "./MobileSamplesRow/utils/sampleHelpers";

const ORANGE = "#FF9015";

export default function MobileFavoritesRow() {
  const dispatch = useDispatch();

  const sampleShifts = useSelector((state) => state.sampleShifts.list);
  const draggingItem = useSelector((state) => state.planning.draggingItem);
  const dynamicLeaves = useSelector((state) => state.sampleLeaves.dynamicLeaves);

  const [ghostPos, setGhostPos] = useState(null);
  const ghostShiftRef = useRef(null);
  const draggingRef = useRef(false);

  const favoriteShifts = useMemo(
    () => (sampleShifts || []).filter((s) => s.is_favorite),
    [sampleShifts]
  );

  // ── Drag handling (mirrors MobileSamplesRow) ──
  const handleItemPointerDown = (e, shift) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    ghostShiftRef.current = shift;
    dispatch(setDraggingItem(shift));
    setGhostPos({ x: e.clientX, y: e.clientY });
  };

  useEffect(() => {
    if (!ghostPos) return;

    const handleMove = (e) => {
      setGhostPos({ x: e.clientX, y: e.clientY });
    };

    const handleUp = () => {
      setGhostPos(null);
      ghostShiftRef.current = null;
      draggingRef.current = false;
    };

    document.addEventListener("pointermove", handleMove, { passive: true });
    document.addEventListener("pointerup", handleUp);
    document.addEventListener("pointercancel", handleUp);

    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);
      document.removeEventListener("pointercancel", handleUp);
    };
  }, [ghostPos]);

  // ── Drop zone: allow drops from SamplesRow to add a favorite ──
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      if (
        draggingItem &&
        draggingItem.id &&
        draggingItem.task &&
        !draggingItem.is_favorite
      ) {
        dispatch(addFavoriteShiftSample({ id_sample_shift: draggingItem.id }));
      }
    },
    [draggingItem, dispatch]
  );

  // ── Remove favorite ──
  const handleRemoveFavorite = useCallback(
    (shift, e) => {
      e.stopPropagation();
      e.preventDefault();
      dispatch(deleteFavoriteShiftSample({ id_sample_shift: shift.id }));
    },
    [dispatch]
  );

  const ghostShift = ghostShiftRef.current;

  return (
    <Box sx={{ mt: 0 }}>
      {/* Label */}
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#e8a020",
          px: 1.5,
          pt: 0.5,
          pb: 0.25,
          letterSpacing: "0.3px",
        }}
      >
        ★ Favoris
      </Typography>

      {/* Chips row */}
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";
        }}
        onDrop={handleDrop}
        sx={{
          display: "flex",
          alignItems: "center",
          overflowX: "auto",
          gap: 0.75,
          px: 1.5,
          pt: 0.25,
          pb: 0.75,
          minHeight: 44,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          WebkitOverflowScrolling: "touch",
          flexShrink: 0,
        }}
      >
        {favoriteShifts.length === 0 && (
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: "text.secondary",
              fontStyle: "italic",
              py: 0.5,
            }}
          >
            Glissez vos shifts ici pour les ajouter aux favoris
          </Typography>
        )}

        {favoriteShifts.map((shift) => {
          const color = getSampleColor(shift);
          const label = getSampleLabel(shift, dynamicLeaves);
          const isActive = draggingItem && draggingItem.id === shift.id;
          const timeLabel =
            shift.timeFrom != null && shift.timeTo != null
              ? `${formatMinutes(shift.timeFrom)}-${formatMinutes(shift.timeTo)}`
              : "";

          return (
            <Box
              key={`fav-${shift.id}`}
              sx={{
                position: "relative",
                flexShrink: 0,
              }}
            >
              {/* Chip (same styling as SampleChip) */}
              <Box
                onPointerDown={(e) => handleItemPointerDown(e, shift)}
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  px: 1,
                  py: 0.5,
                  pr: 2.5, // extra space for remove button
                  minWidth: 52,
                  minHeight: 36,
                  cursor: "grab",
                  touchAction: "none",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  backgroundColor: color,
                  border: isActive
                    ? "2px solid rgba(255,255,255,0.8)"
                    : "2px solid transparent",
                  boxShadow: isActive
                    ? `0 0 0 2px ${color}, 0 4px 12px rgba(0,0,0,0.2)`
                    : "0 1px 4px rgba(0,0,0,0.12)",
                  opacity: isActive ? 0.7 : 1,
                  transform: isActive ? "scale(0.95)" : "scale(1)",
                  transition:
                    "box-shadow 0.15s ease, transform 0.15s ease, opacity 0.15s ease",
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: "#fff",
                    textAlign: "center",
                    maxWidth: 70,
                  }}
                >
                  {label}
                </Typography>
                {timeLabel && (
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "0.55rem",
                      fontWeight: 500,
                      lineHeight: 1.1,
                      color: "rgba(255,255,255,0.85)",
                      textAlign: "center",
                    }}
                  >
                    {timeLabel}
                  </Typography>
                )}
              </Box>

              {/* Remove button */}
              <IconButton
                size="small"
                onClick={(e) => handleRemoveFavorite(shift, e)}
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  width: 20,
                  height: 20,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.7)",
                  },
                  "&:active": {
                    transform: "scale(0.9)",
                  },
                  zIndex: 2,
                }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      {/* Drag ghost (portal to body) */}
      {ghostPos &&
        ghostShift &&
        createPortal(
          <Box
            sx={{
              position: "fixed",
              left: ghostPos.x - 30,
              top: ghostPos.y - 40,
              zIndex: 9999,
              pointerEvents: "none",
              borderRadius: "8px",
              px: 1,
              py: 0.5,
              backgroundColor: getSampleColor(ghostShift),
              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
              transform: "scale(1.08)",
              minWidth: 52,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.92,
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#fff",
                textShadow: "0 1px 2px rgba(0,0,0,0.4)",
              }}
            >
              {getSampleLabel(ghostShift, dynamicLeaves)}
            </Typography>
          </Box>,
          document.body
        )}
    </Box>
  );
}
